/**
 * Helper reutilizable para obtener nombres de empresas por ID.
 * Usado en listados admin (productos, campañas) para mostrar nombre en lugar de UID.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

const FALLBACK_SIN_NOMBRE = 'Empresa sin nombre';
const FALLBACK_NO_ENCONTRADA = 'Empresa no encontrada';

/**
 * Extrae el nombre visible de un documento de empresa (cualquier variante usada en el proyecto).
 * @param {import('firebase/firestore').DocumentSnapshot} snap
 * @returns {string}
 */
function extractNombreFromEmpresaDoc(snap) {
  if (!snap?.exists?.()) return FALLBACK_NO_ENCONTRADA;
  const d = snap.data();
  const name =
    d?.nombre ??
    d?.nombre_empresa ??
    d?.nombreEmpresa ??
    d?.razonSocial ??
    d?.razon_social ??
    '';
  return (typeof name === 'string' && name.trim()) ? name.trim() : FALLBACK_SIN_NOMBRE;
}

/**
 * Obtiene un mapa idEmpresa -> nombre de empresa para una lista de IDs.
 * Una sola ronda de lecturas (getDoc por ID); sin consultas por fila.
 * @param {string[]} ids - IDs de documentos en colección empresas
 * @returns {Promise<Map<string, string>>} Mapa id -> nombre
 */
export async function getEmpresasNombresByIds(ids) {
  const unique = [...new Set(ids)].filter(Boolean);
  if (unique.length === 0) return new Map();
  const snapshots = await Promise.all(unique.map((id) => getDoc(doc(db, 'empresas', id))));
  const map = new Map();
  unique.forEach((id, i) => {
    const name = extractNombreFromEmpresaDoc(snapshots[i]);
    map.set(id, name);
  });
  return map;
}

/**
 * Obtiene el nombre de una empresa por ID (una sola lectura).
 * @param {string} empresaId
 * @returns {Promise<string>}
 */
export async function getEmpresaNombreById(empresaId) {
  if (!empresaId) return FALLBACK_SIN_NOMBRE;
  const snap = await getDoc(doc(db, 'empresas', empresaId));
  return extractNombreFromEmpresaDoc(snap);
}

/**
 * Enriquece una lista de ítems con _displayEmpresa resolviendo por empresaId/idEmpresa cuando falta nombre.
 * Modifica cada ítem in-place: asigna _displayEmpresa (nombre o fallback).
 * @param {Array<{ empresaId?: string, idEmpresa?: string, empresaNombre?: string, nombreEmpresa?: string, [key: string]: any }>} items
 * @param {(item: any) => string} [getId] - opcional: (item) => item.empresaId || item.idEmpresa
 * @param {(item: any) => string} [getNombreExistente] - opcional: (item) => item.empresaNombre ?? item.nombreEmpresa
 */
export async function enriquecerConNombreEmpresa(items, getId, getNombreExistente) {
  const defaultGetId = (item) => item.empresaId ?? item.idEmpresa ?? null;
  const defaultGetNombre = (item) =>
    (item.empresaNombre ?? item.nombreEmpresa ?? '').trim() || null;
  const idFn = typeof getId === 'function' ? getId : defaultGetId;
  const nameFn = typeof getNombreExistente === 'function' ? getNombreExistente : defaultGetNombre;

  const idsToResolve = items
    .map((item) => idFn(item))
    .filter(Boolean)
    .filter((id, idx, arr) => arr.indexOf(id) === idx);
  const map = await getEmpresasNombresByIds(idsToResolve);

  items.forEach((item) => {
    const nombreExistente = nameFn(item);
    if (nombreExistente) {
      item._displayEmpresa = nombreExistente;
      return;
    }
    const id = idFn(item);
    item._displayEmpresa = id ? (map.get(id) ?? FALLBACK_SIN_NOMBRE) : '—';
  });
}
